import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Input, Segmented, Select, Space, App as AntdApp } from 'antd';
import { DndContext, DragOverlay, KeyboardSensor, PointerSensor, defaultKeyboardCoordinateGetter, useSensor, useSensors } from '@dnd-kit/core';

import ContextBar from '../common/ContextBar';
import { DataState, Skeleton, deriveDataState } from '../loading';
import FormActionsFooter from '../common/FormActionsFooter';
import ToggleRow from '../common/ToggleRow';
import UndoRedoButtons from '../common/UndoRedoButtons';
import PageLayoutRender from './PageLayoutRender';
import { BlockHandles, NUDGE_HELP_ID, PALETTE, PalettePill } from './PageEditorHandles';
import PageBlockInspector from './PageBlockInspector';
import PageFormPanel from './PageFormPanel';
import PagePaperControls from './PagePaperControls';
import { CARRIED, pointerOf, sheetsOnCanvas } from './pageCanvasPoint';
import usePageFormWrites from './usePageFormWrites';
import PageFitNotice from './PageFitNotice';
import { formSampleItem } from './pageFormSample';
import useBlockFit from '../../hooks/useBlockFit';
import { collectionCrumbs } from '../../utils/breadcrumbs';
import { useCardDefinitions } from '../../contexts/CardDefinitionsContext';
import { isRepeatingGroup } from '../../config/repeatingGroups';
import { elementLabel } from '../items/itemValueDisplay';
import { normalizeId } from '../../utils/ids';
import { ShellActions, ShellSlotContext, useShellSlot } from '../shell/ShellSlot';
import { collectionCardPath } from '../../config/collectionShell';
import { GROUP_DRAG, dragGroup, fineStep, groupDragNote } from './pageGroupLines';
import {
	BLOCK_KINDS,
	FIELD_BLOCK_KINDS,
	GRID_SIZES,
	NO_GUIDES,
	bringBlocksOntoForm,
	emptyBody,
	gridCoordinateGetter,
	gridLabel,
	gridStep,
	moveBlockAligned,
	moveBlockToSheet,
	nudgeBlock,
	nudgeStep,
	placeBlockAligned,
	pointToUnits,
	pxPerUnit,
	removeBlock,
	resizeBlockAligned,
	setSubject,
	shiftNudgeStep,
	toBody,
} from './pageLayoutModel';
import { blockPage, formSheets, hasForm, offFormBlocks, sheetAtPoint, sheetPage } from './pageLayoutForm';
import { createPageLayout, getPageLayouts, updatePageLayout } from '../../services/cardLayouts';
import { useDirtyForm } from '../../hooks/useDirtyForm';
import usePageEditorView from '../../hooks/usePageEditorView';
import useUndoHistory from '../../hooks/useUndoHistory';
import useUndoShortcuts from '../../hooks/useUndoShortcuts';
import { useScreenShortcuts } from '../../contexts/ShortcutsContext';
import useElementChoices from '../../hooks/useElementChoices';
import LayoutQueryFilter from './LayoutQueryFilter';
import { withElementChoicesField } from '../../utils/elementRefusal';
import { buildFilterFields } from '../../utils/kqlBuilder';
import QueryRefusalPick from '../items/QueryRefusalPick';
import useWindowClass from '../../hooks/useWindowClass';
import useFormPageImages from '../../hooks/useFormPageImages';

/**
 * The canvas: drag element pills onto a sheet, move and resize them, style
 * them, and set the paper.
 *
 * The canvas IS `PageLayoutRender` with `interactive` — the same component the
 * print route uses, at the same physical size — so what is being arranged here
 * is literally what comes out of the printer. Nothing is previewed; the thing
 * itself is edited.
 *
 * Every rule lives in pageLayoutModel.js, and a drag is arithmetic: a CSS inch
 * is 96px by definition, so a pixel delta converts to page units exactly with
 * no measuring. Each palette pill also INSERTS ON CLICK, the affordance
 * DisplayTemplateEditor established — a drag cannot be driven in jsdom, and
 * keyboard users need the same door.
 *
 * A layout with a form (KPR-1860) has one sheet per form page. A form changes on
 * the server at once (PageFormPanel), so it is never an undo step: the answer
 * goes through `rebase`, into every step, and no undo can bring back a paper the
 * API refuses. A palette drop lands on the sheet under the pointer; a click lands
 * on the page chosen above the sheet, which is also what clicking a sheet picks.
 */

// What a screen reader is told about a block it can pick up. dnd-kit's own
// announcements read out internal ids; the editor's live region says where the
// block is in the page's own unit instead.
const SILENT = () => undefined;
const DND_ACCESSIBILITY = {
	screenReaderInstructions: {
		draggable: 'Press Space to pick it up, the arrow keys to move it, Space to drop it and Escape to put it back.',
	},
	announcements: { onDragStart: SILENT, onDragMove: SILENT, onDragOver: SILENT, onDragEnd: SILENT, onDragCancel: SILENT },
};

// The sheet the editor draws for a layout whose subject is a query (KPR-992).
// PageLayoutRender draws one sheet per entry of `items` for a query layout, and
// the editor runs no query: there are no records to arrange against, only the
// page. One entry that is no record at all gives the same single, valueless
// sheet a record layout gets from `item={null}`, so 'One record' and 'A query'
// look and behave alike on the canvas (the key is `sheet-0` in both, so
// switching between them redraws nothing). It is never stored: Save sends
// `toBody(body)` and nothing else. Frozen and module-level, so its identity is
// stable between renders.
const EDITOR_SHEET = Object.freeze([null]);

// A layout that is gone, or is not this person's, answers 403/404 (or is simply
// absent from the card's list). Anything else - no network, a 5xx - is a
// failure to read it, and is worth another try (docs/platform-standards.md §14).
const isMissing = (error) => error?.message === 'not-found' || [403, 404].includes(error?.response?.status);

export default function PageLayoutEditorPage() {
	const { collectionId, cardId, layoutId } = useParams();
	const navigate = useNavigate();
	const { message } = AntdApp.useApp();
	const { cardsById, getElementsForCard, loading: cardsLoading } = useCardDefinitions();
	// Inside the collection shell (KPR-1287): the collection's header is drawn
	// above, Cards current; this page says its crumbs and portals its actions.
	// Bare (no layout above it) it draws its own bar, as before.
	const inShell = Boolean(useContext(ShellSlotContext)) && Boolean(collectionId);
	const { compact: phone } = useWindowClass();
	// Cancel and a landed save return to the card's Display section, where the
	// page layouts are offered, in the collection the editor was opened from.
	const displayPath = `${collectionCardPath(collectionId, cardId)}?tab=display`;

	const isNew = !layoutId;
	const [name, setName] = useState('');
	// The layout being arranged, with its undo history (KPR-348). The history is
	// this component's own, so it ends when the editor closes; a stored layout
	// arriving is a RESET (the baseline), never a step.
	const { present: body, commit, current, undo, redo, reset, rebase, canUndo, canRedo } = useUndoHistory(emptyBody);
	const [selectedId, setSelectedId] = useState(null);
	const [scope, setScope] = useState('card');
	// `loaded` is "the stored layout has answered": until it has, the page draws
	// a skeleton (or the failure with Retry), never the editor, so a blank draft
	// can neither be edited nor saved over the real layout.
	const [loaded, setLoaded] = useState(false);
	const [saving, setSaving] = useState(false);
	const savingRef = useRef(false);
	savingRef.current = saving;
	const [loadError, setLoadError] = useState(null);
	const [loadAttempt, setLoadAttempt] = useState(0);
	// Bumped when a stored layout lands, so what loaded is the baseline the
	// unsaved-changes guard measures edits against.
	const [seedVersion, setSeedVersion] = useState(0);
	const dirtySnapshot = useMemo(() => ({ name, body, scope }), [name, body, scope]);
	const { isDirty, resetDirty, guardDiscard } = useDirtyForm(dirtySnapshot, { baselineKey: seedVersion });
	const handleCancel = () => guardDiscard(() => navigate(displayPath));

	const card = cardsById?.[normalizeId(cardId)] || null;
	// All collections › {collection} › {card} › Page layout: the card links to its page.
	const cardName = card?.name || 'Card';
	useShellSlot({
		crumbTail: [{ label: cardName, to: collectionCardPath(collectionId, cardId) }, { label: 'Page layout' }],
		off: !inShell,
	});
	const elementDefs = useMemo(() => (getElementsForCard ? getElementsForCard(cardId) : []), [getElementsForCard, cardId]);
	// What a layout's query can ask about: every card the page can read, because
	// the same layout prints from every collection that uses its card and its
	// query is compiled there. An element is picked by its id (element ids step
	// 3), the same element wherever it is read; a card key in stored text is kept
	// as written (keepKeys). The `card` field is offered here, unlike on a list:
	// no toolbar picks the card of a layout, its query does.
	const queryFields = useMemo(() => {
		const cards = Object.values(cardsById || {});
		return buildFilterFields({ cards, cardIds: new Set(cards.map(card => normalizeId(card?._id))), getElementsForCard, keepKeys: true })
			.map(field => (field.key === 'card' ? { ...field, notOffered: false } : field));
	}, [cardsById, getElementsForCard]);
	// A card-tier layout is read in every collection that uses the card, so an
	// item of THIS collection means nothing in the others: its lookup rows keep a
	// text box. A collection or personal layout is about this collection.
	const queryCollectionId = scope === 'card' ? null : collectionId;

	// The form's pages, and which of them a click adds to.
	const formImages = useFormPageImages({ cardId, layoutId, form: body.form, collectionId });
	const sheets = formSheets(body);
	const onForm = hasForm(body);
	const [pickedPage, setPickedPage] = useState(1);
	const addPage = Math.min(pickedPage, sheets.length);
	// A form action in flight: Save waits for it (PageFormPanel).
	const [formBusy, setFormBusy] = useState(false);
	// The address a new layout took on when an upload saved it: this page already
	// holds that layout, and reading it again would drop the upload's progress.
	const adoptedRef = useRef(null);

	useEffect(() => {
		if (isNew) return undefined;
		if (adoptedRef.current === `${layoutId}:${loadAttempt}`) return undefined;
		let active = true;
		setLoaded(false);
		setLoadError(null);
		getPageLayouts(cardId, { collectionId })
			.then(layouts => {
				if (!active) return;
				const found = layouts.find(row => normalizeId(row._id) === normalizeId(layoutId));
				if (!found) throw new Error('not-found');
				setName(found.name || '');
				reset(found.body);
				setScope(found.scope);
				setSeedVersion(version => version + 1);
				setLoaded(true);
			})
			.catch(e => { if (active) setLoadError(e); });
		return () => { active = false; };
	}, [isNew, cardId, collectionId, layoutId, loadAttempt, reset]);

	const paletteLabel = (paletteId) => {
		if (!paletteId.startsWith('el:')) return paletteId[0].toUpperCase() + paletteId.slice(1);
		const def = elementDefs.find(d => d?.name === paletteId.slice(3));
		return def ? elementLabel(def) : paletteId.slice(3);
	};

	const selected = body.blocks.find(block => block.id === selectedId) || null;
	const placedElements = new Set(body.blocks.filter(b => FIELD_BLOCK_KINDS.includes(b.type)).map(b => b.element));

	// The canvas aids (KPR-345): snap, the grid's size and the rulers, kept per
	// person in this browser. `step` is THE grid step in the page's own unit, or
	// null with snap off, and it is what every call below hands the model: the
	// preview, the drop, a palette landing and a click-insert all snap the same
	// way, so the block shown is the block that lands.
	const { view, setView } = usePageEditorView();
	const pageUnit = body.page.unit;
	const step = view.snap ? gridStep(pageUnit, view.grid) : null;

	// Alignment guides (KPR-348) are the person's to switch off, so a block can
	// be placed a hair from another one's edge.
	const align = view.guides;
	// On a form a repeating group goes on the form's ruled lines (KPR-1863); on a
	// plain sheet it stays an element block, its entries as a table.
	const blockSpec = (paletteId, formPage = 1) => {
		const def = paletteId.startsWith('el:') ? elementDefs.find(candidate => candidate?.name === paletteId.slice(3)) : null;
		const kind = onForm && isRepeatingGroup(def) ? { type: 'group', group: def } : { type: 'element' };
		return {
			...(paletteId.startsWith('el:') ? { ...kind, element: paletteId.slice(3) } : { type: paletteId }),
			...(formPage > 1 ? { formPage } : {}),
		};
	};

	// A click puts the block at its default place; a drop puts it where it was
	// let go (`at` on the grid, `rawAt` off it, which the guides are measured from).
	const insert = useCallback((paletteId, at = null, rawAt = null, formPage = addPage) => {
		const placed = placeBlockAligned(current(), blockSpec(paletteId, formPage), { at, rawAt, step, align });
		commit(placed.body, { label: 'add' });
		setSelectedId(placed.blockId);
	}, [current, commit, step, align, addPage, onForm, elementDefs]); // eslint-disable-line react-hooks/exhaustive-deps

	// With snap on, one arrow press carries a block one grid step (dnd-kit's
	// own 25px is no multiple of any grid); with it off the sensor is as it was.
	// A group's line or tilt moves a fine step at a time, whatever the grid.
	const keyboardOptions = useMemo(() => {
		const ppu = pxPerUnit(pageUnit);
		const grid = step ? gridCoordinateGetter(step * ppu) : defaultKeyboardCoordinateGetter;
		const fine = gridCoordinateGetter(fineStep(pageUnit) * ppu);
		return { coordinateGetter: (event, args) => (/^grp:(line|tilt):/.test(String(args.active)) ? fine : grid)(event, args) };
	}, [step, pageUnit]);
	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
		// Arrow keys move a grabbed block (Space to pick up and drop, Escape to
		// cancel). The grab handle was focusable but could only select.
		useSensor(KeyboardSensor, keyboardOptions),
	);

	// The drag in flight, or null. Nothing here is stored: the sheet is drawn
	// from `previewBody` while it is set, and the stored body changes only on
	// drop, through the same model call the preview used — so what was shown
	// is what lands, rounding and clamping included.
	const [drag, setDrag] = useState(null);

	const dragState = useCallback((event) => {
		const id = String(event.active.id);
		// onDragStart carries no delta yet: the drag is where it began.
		const { x: dx, y: dy } = event.delta || { x: 0, y: 0 };
		let at = null;
		let rawAt = null;
		let formPage = 1;
		let crossing = null;
		if (id.startsWith(PALETTE)) {
			const pointer = pointerOf(event);
			const onCanvas = sheetsOnCanvas();
			// Off every sheet the first one answers, and its own test says "not over
			// the page": what a drop beside a plain layout's one sheet always did.
			const sheet = pointer ? (sheetAtPoint(onCanvas, pointer.x, pointer.y) || onCanvas[0]) : null;
			if (sheet) {
				const { rect } = sheet;
				const paper = sheetPage(body, sheet.formPage);
				({ formPage } = sheet);
				at = pointToUnits(pointer.x - rect.left, pointer.y - rect.top, paper, step);
				rawAt = pointToUnits(pointer.x - rect.left, pointer.y - rect.top, paper, null, { exact: true });
			}
		} else if (id.startsWith('move:') && sheets.length > 1) {
			// Where the block is being carried to, from where it sits and how far it
			// has come. Not dnd-kit's rectangle: that follows the block as the preview
			// redraws it, and would count the carry twice. The block's middle decides
			// which sheet it is over, as it does for the person looking at it.
			const block = body.blocks.find(b => b.id === id.slice(5));
			const onCanvas = sheetsOnCanvas();
			const from = block ? onCanvas.find(sheet => sheet.formPage === blockPage(block)) : null;
			if (from) {
				const ppu = pxPerUnit(body.page.unit);
				const left = from.rect.left + block.x * ppu + dx;
				const top = from.rect.top + block.y * ppu + dy;
				const sheet = sheetAtPoint(onCanvas, left + (block.w * ppu) / 2, top + ((block.h || 0) * ppu) / 2);
				if (sheet && sheet !== from) {
					const paper = sheetPage(body, sheet.formPage);
					const x = Math.max(0, left - sheet.rect.left);
					const y = Math.max(0, top - sheet.rect.top);
					const point = pointToUnits(x, y, paper, step);
					if (point) crossing = { formPage: sheet.formPage, at: point, rawAt: pointToUnits(x, y, paper, null, { exact: true }) };
				}
			}
		}
		return { id, dx, dy, at, rawAt, formPage, crossing };
	}, [body, sheets.length, step]);

	// What the sheet shows while a drag is held, and the guides drawn over it.
	// The drop below calls the very same model functions, so what was shown is
	// what lands (KPR-335), guides included.
	const { previewBody, ghostId, guides } = useMemo(() => {
		const none = { previewBody: body, ghostId: null, guides: NO_GUIDES };
		if (!drag) return none;
		// The sheet a guide is drawn on: the one the block is on, or will land on.
		const onSheet = (lines, formPage) => ({ ...lines, formPage });
		if (drag.id.startsWith('move:') && drag.crossing) {
			const id = drag.id.slice(5);
			const landed = moveBlockToSheet(body, id, drag.crossing.formPage, { ...drag.crossing, step, align });
			const ghost = { ...landed.body.blocks.find(b => b.id === id), id: CARRIED };
			return { previewBody: { ...body, blocks: [...body.blocks, ghost] }, ghostId: CARRIED, guides: onSheet(landed.guides, drag.crossing.formPage) };
		}
		if (drag.id.startsWith('move:')) {
			const moved = moveBlockAligned(body, drag.id.slice(5), drag.dx, drag.dy, step, { align });
			return { previewBody: moved.body, ghostId: null, guides: onSheet(moved.guides, blockPage(moved.body.blocks.find(b => b.id === drag.id.slice(5)))) };
		}
		if (drag.id.startsWith(GROUP_DRAG)) {
			return { ...none, previewBody: dragGroup(body, drag.id, drag.dx / pxPerUnit(pageUnit), drag.dy / pxPerUnit(pageUnit), step) };
		}
		if (drag.id.startsWith('size:')) {
			const sized = resizeBlockAligned(body, drag.id.slice(5), drag.dx, drag.dy, step, { align });
			return { previewBody: sized.body, ghostId: null, guides: onSheet(sized.guides, blockPage(sized.body.blocks.find(b => b.id === drag.id.slice(5)))) };
		}
		if (drag.id.startsWith(PALETTE) && drag.at) {
			const placed = placeBlockAligned(body, blockSpec(drag.id.slice(PALETTE.length), drag.formPage), { at: drag.at, rawAt: drag.rawAt, step, align });
			return { previewBody: placed.body, ghostId: placed.blockId, guides: onSheet(placed.guides, drag.formPage) };
		}
		return none;
	}, [body, drag, step, align, onForm, elementDefs]); // eslint-disable-line react-hooks/exhaustive-deps

	// On a form the designer draws a sample value in each block and says which
	// ones do not fit their box (KPR-1861). Paused while a block is carried, and
	// measured only once the editor is on the screen. A plain layout runs none of it.
	const sample = useMemo(() => (onForm ? formSampleItem(elementDefs) : null), [onForm, elementDefs]);
	const sampleSheet = useMemo(() => (sample ? [sample] : EDITOR_SHEET), [sample]);
	const [canvasRoot, setCanvasRoot] = useState(null);
	const cuts = useBlockFit(canvasRoot, { enabled: onForm && (isNew || loaded) && !cardsLoading, paused: drag !== null });
	const cutById = useMemo(() => new Map(cuts.map(cut => [cut.id, cut.text])), [cuts]);

	// What the live region says: the last undo, redo or nudge. `n` remounts the
	// text, so the same sentence twice (two presses at the page edge) is heard twice.
	const [note, setNote] = useState({ text: '', n: 0 });
	const announce = useCallback(text => setNote(last => ({ text, n: last.n + 1 })), []);

	// Every other change to the sheet (the inspector, the paper, a removal) is a
	// step too. A `key` joins consecutive changes of one control into one step.
	const change = useCallback((fn, label, key = null) => commit(fn, { label, key }), [commit]);

	const handleDragEnd = useCallback((event) => {
		const { active } = event;
		const end = active && event.delta ? dragState(event) : null;
		setDrag(null);
		if (!end) return;
		const { id, dx, dy, at, rawAt, formPage, crossing } = end;
		if (id.startsWith(PALETTE)) {
			// A palette drag lands where it was released, in page coordinates: on
			// the sheet under it, or at the page a click adds to if on no sheet.
			insert(id.slice(PALETTE.length), at, rawAt, at ? formPage : addPage);
			announce(at ? 'Added to the page.' : 'Added below the last block.');
			return;
		}
		if (id.startsWith(GROUP_DRAG)) {
			const after = commit(latest => dragGroup(latest, id, dx / pxPerUnit(latest.page.unit), dy / pxPerUnit(latest.page.unit), step), { label: 'group change' });
			announce(groupDragNote(after, id));
			return;
		}
		const blockId = id.slice(5);
		if (id.startsWith('move:') && crossing) {
			commit(latest => moveBlockToSheet(latest, blockId, crossing.formPage, { ...crossing, step, align }).body, { label: 'move' });
			setPickedPage(crossing.formPage);
			const landed = current().blocks.find(b => b.id === blockId);
			announce(landed ? `Dropped on page ${crossing.formPage}. Position x ${landed.x} ${current().page.unit}, y ${landed.y} ${current().page.unit}.` : '');
			return;
		}
		if (id.startsWith('move:')) {
			commit(latest => moveBlockAligned(latest, blockId, dx, dy, step, { align }).body, { label: 'move' });
			const landed = current().blocks.find(b => b.id === blockId);
			announce(landed ? `Dropped. Position x ${landed.x} ${current().page.unit}, y ${landed.y} ${current().page.unit}.` : '');
			return;
		}
		if (id.startsWith('size:')) {
			commit(latest => resizeBlockAligned(latest, blockId, dx, dy, step, { align }).body, { label: 'resize' });
			const landed = current().blocks.find(b => b.id === blockId);
			announce(landed ? `Dropped. Size ${landed.w} by ${landed.h ?? 0} ${current().page.unit}.` : '');
		}
	}, [insert, addPage, dragState, step, align, commit, current, announce]);

	// What a screen reader hears while a block is carried: where it is now, in
	// the page's own unit, and when it has snapped to a guide.
	const dragNote = useMemo(() => {
		if (!drag) return '';
		if (drag.id.startsWith(GROUP_DRAG)) return groupDragNote(previewBody, drag.id);
		const unitName = body.page.unit;
		const block = ghostId ? previewBody.blocks.find(b => b.id === ghostId) : previewBody.blocks.find(b => drag.id.endsWith(b.id));
		if (!block) return 'Not over the page.';
		const verb = drag.id.startsWith('size:') ? 'Size' : 'Position';
		const onPage = sheets.length > 1 ? `Page ${blockPage(block)}. ` : '';
		const where = drag.id.startsWith('size:')
			? `${onPage}${verb} ${block.w} by ${block.h ?? 0} ${unitName}.`
			: `${onPage}${verb} x ${block.x} ${unitName}, y ${block.y} ${unitName}.`;
		return guides.x.length || guides.y.length ? `${where} Aligned with a guide.` : where;
	}, [drag, previewBody, ghostId, guides, body.page.unit, sheets.length]);

	// A nudge (KPR-348): the arrow keys on a focused block carry it one grid step
	// (the finest the sheet offers with snap off), Shift four of them. Repeats of
	// the same block's nudge within a second are one undo step; a block against
	// the page edge does not move and says so.
	const nudge = useCallback((blockId, direction, big) => {
		// Not while a save is on its way: the page would call it saved.
		if (savingRef.current) return;
		const unitName = current().page.unit;
		const amount = (big ? shiftNudgeStep : nudgeStep)(unitName, view.snap, view.grid);
		const before = current().blocks.find(b => b.id === blockId);
		const after = commit(latest => nudgeBlock(latest, blockId, direction, { amount, step }), { label: 'move', key: `nudge:${blockId}` })
			.blocks.find(b => b.id === blockId);
		if (!before || !after) return;
		setSelectedId(blockId);
		const where = `Position x ${after.x} ${unitName}, y ${after.y} ${unitName}.`;
		announce(before.x === after.x && before.y === after.y ? `${where} It cannot move further that way.` : where);
	}, [current, commit, step, view.snap, view.grid, announce]);

	// Undo and redo (KPR-348). Never while a block is in the air, or the sheet
	// would change under the gesture; and never while a save is on its way, or
	// the page would call a body clean that is not the one the server received.
	const stepping = drag === null && !saving;
	const handleUndo = useCallback(() => {
		const done = undo();
		announce(done ? `Undid ${done.label}.` : 'Nothing to undo.');
	}, [undo, announce]);
	const handleRedo = useCallback(() => {
		const done = redo();
		announce(done ? `Redid ${done.label}.` : 'Nothing to redo.');
	}, [redo, announce]);
	// The keys are the editor's unless a text field has them, or a dialog or
	// dropdown is open over the page.
	useUndoShortcuts({
		enabled: stepping,
		onUndo: handleUndo,
		onRedo: handleRedo,
		accepts: target => !(target?.closest && target.closest('[role="dialog"], .ant-modal-root, .ant-select-dropdown, .ant-popover, .ant-dropdown')),
	});
	useScreenShortcuts('Page layout editor', [
		{ keys: ['mod', 'Z'], label: 'Undo' },
		{ keys: ['mod', 'shift', 'Z'], label: 'Redo' },
		{ keys: ['↑ ↓ ← →'], label: 'Nudge the focused block one step' },
		{ keys: ['shift', '↑ ↓ ← →'], label: 'Nudge the focused block four steps' },
	]);

	// An element name a query (the layout's or a block's) could mean several
	// of (400 `ambiguous_element`, element ids step 3): the person picks one,
	// or any of these cards, and the layout is saved again with the pick.
	// …answering exactly the queries it was made for: any change forgets it.
	const elementPicks = useElementChoices(JSON.stringify([body.query || '', (body.blocks || []).map(block => (block && block.type === 'query' ? [block.query || '', block.linkElement || ''] : null))]));

	const { ensureSaved, formLanded } = usePageFormWrites({
		cardId, layoutId, collectionId, isNew, isDirty, scope, snapshot: dirtySnapshot, current, rebase, resetDirty, elementPicks, loadAttempt, adoptedRef,
		onAdopted: (id) => {
			setLoaded(true);
			navigate(`/collections/${collectionId}/cards/${cardId}/layouts/${id}/edit`, { replace: true });
		},
		onClean: () => setSeedVersion(version => version + 1),
		onBusy: setFormBusy,
	});

	const submit = async (elementChoices) => {
		if (!name.trim()) { message.error('Give the layout a name.'); return; }
		setSaving(true);
		try {
			const payload = withElementChoicesField({ scope, collectionId: scope === 'card' ? null : collectionId, name: name.trim(), body: toBody(body) }, elementChoices);
			if (isNew) {
				await createPageLayout(cardId, payload);
				message.success('Layout saved.');
				elementPicks.reset();
				resetDirty();
				navigate(displayPath);
			} else {
				await updatePageLayout(cardId, layoutId, withElementChoicesField({ name: name.trim(), body: toBody(body) }, elementChoices));
				message.success('Layout saved.');
				elementPicks.reset();
				resetDirty();
				navigate(displayPath);
			}
		} catch (error) {
			if (!elementPicks.take(error)) message.error(error?.response?.data?.message || 'Saving the layout failed.');
		} finally {
			setSaving(false);
		}
	};
	const save = () => elementPicks.attempt(submit);

	const awaitingLayout = !isNew && !loaded;
	if (awaitingLayout && loadError && isMissing(loadError)) {
		return (
			<Alert
				type="error"
				showIcon
				message="That layout is not available."
				description="It may have been deleted, or belong to someone else. Go back and pick another."
				action={<Button onClick={() => navigate(displayPath)}>Go to card</Button>}
			/>
		);
	}
	if (awaitingLayout || cardsLoading) {
		return (
			<DataState
				state={deriveDataState({
					loading: (awaitingLayout && !loadError) || cardsLoading,
					error: awaitingLayout && loadError ? 'Check your connection and try again.' : null,
				})}
				skeleton={<Skeleton variant="form-field" count={4} />}
				error={awaitingLayout && loadError ? 'Check your connection and try again.' : null}
				onRetry={() => setLoadAttempt(n => n + 1)}
				label="this page layout"
			/>
		);
	}

	const { page } = body;
	const offForm = offFormBlocks(body);
	// A page a click adds to is chosen here, and it brings its sheet into view.
	const pickPage = (number) => {
		setPickedPage(number);
		document.querySelector(`.page-sheet[data-form-page="${number}"]`)?.scrollIntoView?.({ block: 'nearest' });
	};

	return (
		<>
			{inShell ? (
				// The shell's actions slot: Cancel and Save. A phone's header holds
				// Save alone (Cancel is at the foot), as on the card's page (KPR-1282).
				<ShellActions>
					<Space>
						{phone ? null : <Button onClick={handleCancel}>Cancel</Button>}
						<Button type="primary" loading={saving} disabled={formBusy} onClick={save}>Save</Button>
					</Space>
				</ShellActions>
			) : (
				<ContextBar
					breadcrumb={collectionCrumbs(null, collectionId, { label: cardName })}
					title={isNew ? 'New page layout' : name || 'Page layout'}
					actions={(
						<Space>
							<Button onClick={handleCancel}>Cancel</Button>
							<Button type="primary" loading={saving} disabled={formBusy} onClick={save}>Save</Button>
						</Space>
					)}
				/>
			)}

			<DndContext
				sensors={sensors}
				accessibility={DND_ACCESSIBILITY}
				onDragStart={event => { announce(''); setDrag(dragState(event)); }}
				onDragMove={event => setDrag(dragState(event))}
				onDragEnd={handleDragEnd}
				onDragCancel={() => { setDrag(null); announce('Put back.'); }}
			>
				<div className={`page-editor${onForm ? ' page-editor--form' : ''}`}>
					<div className="page-editor__side">
						<div className="micro-label">Fields</div>
						{elementDefs.filter(def => def?.name).map(def => (
							<PalettePill
								key={def.name}
								id={`el:${def.name}`}
								label={elementLabel(def)}
								hint={placedElements.has(def.name) ? 'On the page' : null}
								onInsert={insert}
							/>
						))}
						<div className="micro-label page-editor__side-heading">Blocks</div>
						{BLOCK_KINDS.filter(kind => !FIELD_BLOCK_KINDS.includes(kind)).map(kind => (
							<PalettePill key={kind} id={kind} label={kind[0].toUpperCase() + kind.slice(1)} onInsert={insert} />
						))}
					</div>

					<div className="page-editor__stage">
						<div className="page-editor__toolbar">
							<UndoRedoButtons canUndo={canUndo} canRedo={canRedo} onUndo={handleUndo} onRedo={handleRedo} disabled={!stepping} />
							{sheets.length > 1 ? (
								<Select
									className="page-editor__add-page"
									aria-label="Page new blocks go on"
									value={addPage}
									onChange={pickPage}
									options={sheets.map(({ number }) => ({ value: number, label: `Adding to page ${number}` }))}
								/>
							) : null}
						</div>
						{offForm.length ? (
							<Alert
								type="warning"
								showIcon
								message={`${offForm.length === 1 ? '1 block is' : `${offForm.length} blocks are`} off the ${onForm ? 'form' : 'sheet'}, and may not print.`}
								action={<Button size="small" onClick={() => change(bringBlocksOntoForm, 'move')}>{`Move onto the ${onForm ? 'form' : 'sheet'}`}</Button>}
							/>
						) : null}
						{onForm ? (
							<>
								<PageFitNotice cuts={cuts} pages={sheets.length} sample />
								<p className="page-editor__sample">Each block shows a sample value. The print preview shows what a real record cuts.</p>
							</>
						) : null}
						<div
							ref={setCanvasRoot}
							className="page-editor__canvas"
							onClick={(event) => {
								setSelectedId(null);
								const number = Number(event.target.closest?.('.page-sheet')?.dataset?.formPage);
								if (number) setPickedPage(number);
							}}
						>
							<PageLayoutRender
								layout={{ body: previewBody }}
								card={card}
								elementDefs={elementDefs}
								item={sample}
								items={body.subject === 'query' ? sampleSheet : undefined}
								interactive
								gridStep={step}
								rulers={view.rulers}
								guides={drag ? guides : null}
								formImages={formImages.pages}
								onRetryForm={formImages.retry}
								renderBlock={(block, node) => (block.id === ghostId ? (
									// Where the palette item will land if released now: drawn as a
									// block but not a handle, so it cannot itself be grabbed.
									<div key={block.id} className="page-editor__block page-editor__block--ghost">{node}</div>
								) : (
									<div
										key={block.id}
										className={`page-editor__block${cutById.has(block.id) ? ' page-editor__block--cut' : ''}${block.id === selectedId ? ' page-editor__block--selected' : ''}${drag && drag.id.endsWith(block.id) && !drag.id.startsWith(PALETTE) && !drag.id.startsWith(GROUP_DRAG) ? ' page-editor__block--lifted' : ''}`}
										onClick={event => { event.stopPropagation(); setSelectedId(block.id); setPickedPage(blockPage(block)); }}
									>
										{node}
										<BlockHandles
											block={block}
											page={page}
											sheet={sheetPage(body, blockPage(block))}
											selected={block.id === selectedId}
											carrying={drag !== null}
											cut={cutById.get(block.id) || null}
											group={block.type === 'group' ? elementDefs.find(def => def?.name === block.element) || null : null}
											onSelect={setSelectedId}
											onNudge={nudge}
											onRemove={id => { change(latest => removeBlock(latest, id), 'remove'); setSelectedId(null); }}
										/>
									</div>
								))}
								/>
						</div>
					</div>

					<div className="page-editor__inspector">
						<div className="micro-label">Layout</div>
						<Input
							value={name}
							onChange={event => setName(event.target.value)}
							placeholder="Name this layout"
							aria-label="Layout name"
							maxLength={100}
						/>
						<Segmented
							block
							aria-label="What the layout prints"
							value={body.subject}
							onChange={value => change(latest => setSubject(latest, value), 'subject change')}
							options={[{ value: 'record', label: 'One record' }, { value: 'query', label: 'A query' }]}
						/>
						{body.subject === 'query' ? (
							<LayoutQueryFilter
								label="Layout query"
								query={body.query || ''}
								fields={queryFields}
								collectionId={queryCollectionId}
								emptyText="No conditions yet. Add one to choose which items the layout prints."
								onChange={query => change(latest => ({ ...latest, query }), 'query change', 'layout-query')}
							/>
						) : null}
						<QueryRefusalPick inline refusal={elementPicks.refusal} disabled={saving || formBusy} onPick={elementPicks.retry} onRepick={elementPicks.repick} />

						<PageFormPanel
							cardId={cardId}
							layoutId={layoutId || null}
							collectionId={collectionId}
							body={body}
							unsaved={isNew || isDirty}
							holding={saving}
							ensureSaved={ensureSaved}
							onBusy={setFormBusy}
							onLanded={formLanded}
							onReload={() => guardDiscard(() => setLoadAttempt(n => n + 1))}
						/>

						{/* A form sets the paper: PageFormPanel draws what a person may change of it. */}
						{onForm ? null : <PagePaperControls page={page} disabled={formBusy} change={change} />}

						<div className="micro-label page-editor__side-heading">Grid, rulers and guides</div>
						<ToggleRow
							first
							label="Snap to grid"
							description="Blocks you drag or resize land on grid lines."
							checked={view.snap}
							onChange={snap => setView({ snap })}
						/>
						<Segmented
							block
							value={view.grid}
							onChange={grid => setView({ grid })}
							aria-label="Grid size"
							options={GRID_SIZES.map(size => ({ value: size, label: gridLabel(pageUnit, size) }))}
						/>
						<ToggleRow
							label="Rulers"
							description={`Along the top and left edge, in ${pageUnit === 'mm' ? 'millimetres' : 'inches'}.`}
							checked={view.rulers}
							onChange={rulers => setView({ rulers })}
						/>
						<ToggleRow
							label="Alignment guides"
							description="A block you drag snaps to another block's edge or centre, or to the margin, and a line shows it."
							checked={view.guides}
							onChange={guides => setView({ guides })}
						/>

						{selected ? (
							<PageBlockInspector
								selected={selected}
								page={page}
								sheets={sheets}
								subject={body.subject}
								elementDefs={elementDefs}
								queryFields={queryFields}
								collectionId={queryCollectionId}
								change={change}
								onForm={onForm}
							/>
						) : (
							<p className="form-panel__hint">Pick a block to size and style it.</p>
						)}
					</div>
				</div>
				<DragOverlay dropAnimation={null}>
					{drag && drag.id.startsWith(PALETTE) ? <div className="page-editor__pill page-editor__pill--carried">{paletteLabel(drag.id.slice(PALETTE.length))}</div> : null}
				</DragOverlay>
				<span id={NUDGE_HELP_ID} className="page-editor__sr">Without picking a block up, the arrow keys nudge it one step, a grid step with snap on, and Shift with an arrow nudges it four.</span>
				<div className="page-editor__live" role="status" aria-live="polite"><span key={dragNote ? 'drag' : note.n}>{dragNote || note.text}</span></div>
			</DndContext>

			<FormActionsFooter>
				<Button onClick={handleCancel}>Cancel</Button>
				<Button type="primary" loading={saving} disabled={formBusy} onClick={save}>Save layout</Button>
			</FormActionsFooter>
		</>
	);
}
